import { zodResolver } from "@hookform/resolvers/zod";
import { orderBy } from "firebase/firestore";
import React, { useState } from "react";
import { Button, Card, Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaSave } from "react-icons/fa";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

import { getRouteplannerVersions } from "../../api/api.jsx";
import {
    DroneConfigDocument,
    useDockCollection,
    useDroneConfigDocument,
    useLocationCollection,
    useOrganizationCollection,
    usePayloadCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import BackButton from "../../components/BackButton";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton";
import BooleanFormGroup from "../../components/FormGroups/BooleanFormGroup";
import ReactSelectFormGroup from "../../components/FormGroups/ReactSelectFormGroup";
import SelectFormGroup from "../../components/FormGroups/SelectFormGroup";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import { useUser } from "../../contexts/user_provider";
import urls from "../../urls.jsx";
import {
    commaSeparatedStringOrArraySchema,
    numberFromString,
} from "../../utils/zodUtils.js";

const droneConfigSchema = z
    .object({
        organizationId: z
            .string()
            .min(1, { message: "Organization is required" }),
        locationId: z
            .string()
            .min(1, { message: "Select a location" })
            .refine((value) => value !== "none", {
                message: "Select a location",
            }),
        payloadId: z
            .string()
            .min(1, { message: "Select a payload" })
            .refine((value) => value !== "none", {
                message: "Select a payload",
            }),
        dockId: z.string(),
        docks: z.array(z.string()).min(1, { message: "Set at least one dock" }),
        model: z.enum(["E12", "E13"]),
        augmentation: z.string(),
        navcontrol: z.object({
            control_settings: z.object({
                P_forward: numberFromString.positive(),
                P_sideways: numberFromString.positive(),
                D_forward: numberFromString.positive(),
                D_sideways: numberFromString.positive(),
                Pxy: numberFromString.positive(),
                Dxy: numberFromString.positive(),
                Ixy: numberFromString.positive(),
                Pz: numberFromString.positive(),
                vxy_max: numberFromString.positive(),
                vz_max: numberFromString.positive(),
                landing_speed: numberFromString.positive(),
                dock_landing_speed: numberFromString.positive(),
                a_forward_max: numberFromString.positive(),
                braking_distance_xy: numberFromString.nonnegative(),
                braking_distance_z: numberFromString.nonnegative(),
            }),
            preferences: z.object({
                auto_sleep: z.boolean(),
                auto_sleep_time_sec: numberFromString.positive(),
                auto_shutdown: z.boolean(),
                auto_shutdown_seconds: numberFromString.positive(),
                is_dock_success_based_on_charging: z.boolean(),
                reboot_on_connection_loss: z.boolean(),
                reboot_on_connection_loss_seconds: numberFromString.positive(),
                skip_calibration: z.boolean(),
                use_task_auto_abort: z.boolean(),
                remaining_flight_duration_factor: numberFromString.positive(),
                remaining_flight_duration_margin:
                    numberFromString.nonnegative(),
                remaining_flight_duration_factor_v3:
                    numberFromString.positive(),
                remaining_flight_duration_margin_v3:
                    numberFromString.nonnegative(),
            }),
            preflight_checks: z
                .object({
                    battery_percent_enabled: z.boolean(),
                    min_battery_percentage: numberFromString
                        .int({ message: "Enter a whole number" })
                        .min(0)
                        .max(100),
                    position_stable_enabled: z.boolean(),
                    position_recent_enabled: z.boolean(),
                    position_accurate_enabled: z.boolean(),
                    remote_connected_enabled: z.boolean(),
                    home_position_z_enabled: z.boolean(),
                    hires_exposure_enabled: z.boolean(),
                    hires_exposure_lower_bound: numberFromString
                        .int({ message: "Enter a whole number" })
                        .min(20)
                        .max(33000),
                    hires_exposure_upper_bound: numberFromString
                        .int({ message: "Enter a whole number" })
                        .min(20)
                        .max(33000),
                    current_flightmode_enabled: z.boolean(),
                    cpu_temperature_enabled: z.boolean(),
                    start_close_to_first_waypoint_enabled: z.boolean(),
                    min_disk_available_mb: numberFromString.nonnegative(),
                    tof_sensors_healthy_enabled: z.boolean(),
                })
                .refine(
                    (data) =>
                        data.hires_exposure_upper_bound >=
                        data.hires_exposure_lower_bound,
                    {
                        message:
                            "Hires exposure upper bound must be higher than lower bound",
                        path: ["hires_exposure_upper_bound"],
                    },
                ),
            obstacle_avoidance: z.object({
                enabled: z.boolean(),
                controller: z.object({
                    avoidance_max_speed_xy: numberFromString.positive(),
                    lookahead_distance_m: numberFromString.positive(),
                    progress_timeout_seconds: numberFromString.positive(),
                    wait_for_clear_seconds: numberFromString.positive(),
                }),
                corridor: z
                    .object({
                        planning_margin_m: numberFromString.nonnegative(),
                        width_m: numberFromString.positive(),
                        z_min_m: numberFromString.positive(),
                        z_max_m: numberFromString.positive(),
                    })
                    .refine((data) => data.z_max_m > data.z_min_m, {
                        message: "Z max should be higher than z min",
                        path: ["z_max_m"],
                    }),
                planning_grid: z.object({
                    clearance_xy_m: numberFromString.nonnegative(),
                    clearance_z_m: numberFromString.nonnegative(),
                }),
            }),
        }),
        poscontrol: z.object({
            camera: z
                .object({
                    exposure_gain_offset: numberFromString.int().positive(),
                    max_desired_gain: numberFromString.int().positive(),
                    max_desired_exposure: numberFromString.int().positive(),
                    max_gain: numberFromString
                        .int({ message: "Enter a whole number" })
                        .positive()
                        .max(80000),
                    min_gain: numberFromString
                        .int({ message: "Enter a whole number" })
                        .positive()
                        .min(100),
                })
                .refine((data) => data.max_gain > data.min_gain, {
                    message: "Maximum gain must be higher than minimum gain",
                    path: ["max_gain"],
                })
                .refine((data) => data.max_desired_gain <= data.max_gain, {
                    message:
                        "Maximum desired gain can't be higher than maximum gain",
                    path: ["max_desired_gain"],
                }),
            exposure: z.object({
                marker_msv: numberFromString
                    .int({ message: "Enter a whole number" })
                    .min(0)
                    .max(255),
                tracking_msv: numberFromString
                    .int({ message: "Enter a whole number" })
                    .min(0)
                    .max(255),
            }),
            calculation: z.object({
                allow_1_marker_calculations: z.boolean(),
                ignore_markers_far_above_drone: z.boolean(),
            }),
            debug: z.object({
                debug_save_time: numberFromString.nonnegative(),
                situation_image_save_time: numberFromString.nonnegative(),
            }),
        }),
        payload: z.object({
            camera_settings: z
                .object({
                    focal_length: numberFromString.positive(),
                    max_focus_distance: numberFromString.positive(),
                    min_focus_distance: numberFromString.positive(),
                    sensor_height: numberFromString.positive(),
                    sensor_width: numberFromString.positive(),
                    z_offset: numberFromString,
                    use_front_camera: z.boolean(),
                })
                .refine(
                    (data) =>
                        data.max_focus_distance >= data.min_focus_distance,
                    {
                        message:
                            "Max focus distance must be higher than or equal to min focus distance",
                        path: ["max_focus_distance"],
                    },
                ),
            uploader: z.object({
                is_disabled: z.boolean(),
                upload_strategy: z.enum(["oldest_first", "newest_first"]),
                order_in_flight_folder: z.enum([
                    "random",
                    "oldest_first",
                    "newest_first",
                ]),
                uploads_per_second: numberFromString.nonnegative(),
                upload_priority_ids: commaSeparatedStringOrArraySchema,
            }),
        }),
        powermodule: z.object({
            capacity_based_battery: z.boolean(),
        }),
        routeplanner: z.object({
            version: z.string().refine(
                async (value) => {
                    const versions = await getRouteplannerVersions();
                    return versions.includes(value);
                },
                { message: "Invalid routeplanner version" },
            ),
            buffer_distance: numberFromString,
            max_flight_duration: numberFromString.positive(),
            min_battery_percentage: numberFromString
                .int({ message: "Enter a whole number" })
                .min(0)
                .max(100),
            travel_speed: numberFromString.positive(),
            scan_speed: numberFromString.positive(),
            skip_take_off: z.boolean(),
            takeoff_height: numberFromString.positive(),
            takeoff_buffer: numberFromString,
            resume_interrupted_line_scan: z.boolean(),
            low_speed_flyzone: z.object({
                speed: numberFromString.positive(),
                buffer_distance_xy: numberFromString.nonnegative(),
                buffer_distance_z: numberFromString.nonnegative(),
            }),
        }),
        streamer: z.object({
            enable_hires_stream: z.boolean(),
            enable_payload_stream: z.boolean(),
            enable_qvio_stream: z.boolean(),
        }),
        telemetry: z.object({
            uploader: z.object({
                is_disabled: z.boolean(),
            }),
        }),
    })
    .refine(
        (data) =>
            data.navcontrol.preflight_checks.min_battery_percentage <=
            data.routeplanner.min_battery_percentage,
        {
            message:
                "Preflight minimum battery percentage can't be greater than routeplanner minimum battery percentage",
            path: ["navcontrol", "preflight_checks", "min_battery_percentage"], // Show error at preflight check
        },
    );

function usePrevious(value) {
    const ref = React.useRef();
    React.useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

export default function DroneSettingsPage() {
    const {
        organizationId: originalOrganizationId,
        locationId: originalLocationId,
        droneId,
    } = useParams();
    const { getUserIsAdmin } = useUser();
    const navigate = useNavigate();
    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(droneConfigSchema),
    });
    const [droneConfigPresets] = useDroneConfigDocument("presets", {
        mode: "once",
    });
    const [droneConfig] = useDroneConfigDocument(droneId, { mode: "once" });

    const [organizations] = useOrganizationCollection([orderBy("name", "asc")]);
    const [payloads] = usePayloadCollection([orderBy("name", "asc")]);
    const [isLoading, setIsLoading] = React.useState(false);

    const organizationId = watch("organizationId");
    const previousOrganizationId = usePrevious(organizationId);
    const locationId = watch("locationId");
    const previousLocationId = usePrevious(locationId);
    const docks = watch("docks");
    const model = watch("model");
    const [routeplannerVersions, setRouteplannerVersions] = useState([
        { id: "V3", name: "V3" },
    ]);

    const [locations] = useLocationCollection(organizationId, [
        orderBy("name", "asc"),
    ]);
    const [locationDocks] = useDockCollection(organizationId, locationId, [
        orderBy("name", "asc"),
    ]);

    React.useEffect(() => {
        if (droneId && droneId !== droneId.toUpperCase()) {
            navigate(urls.drone(droneId.toUpperCase()), { replace: true });
        } else if (
            droneConfig &&
            (droneConfig.organizationId !== organizationId ||
                droneConfig.locationId !== locationId)
        ) {
            navigate(
                urls.locationDroneSettings(
                    droneConfig.organizationId,
                    droneConfig.locationId,
                    droneId.toUpperCase(),
                ),
                { replace: true },
            );
        }

        async function updateRouteplannerVersions() {
            setRouteplannerVersions(
                await getRouteplannerVersions().then((versions) =>
                    versions.map((version) => {
                        return { id: version, name: version };
                    }),
                ),
            );
        }
        updateRouteplannerVersions();
    }, [droneConfig, organizationId, locationId, droneId, navigate]);

    React.useEffect(() => {
        setIsLoading(true);
        if (droneConfig) {
            droneConfig.docks = droneConfig.docks.map((dock) => dock.id);
            reset(droneConfig);
            setIsLoading(false);
        }
    }, [droneConfig, reset]);

    React.useEffect(() => {
        if (
            organizationId &&
            previousOrganizationId &&
            organizationId !== previousOrganizationId
        ) {
            setValue("locationId", "none");
            const keptDocks = docks.filter((dockId) => dockId === "mobile");
            setValue("docks", keptDocks);
        }
    }, [organizationId, previousOrganizationId, docks, setValue]);

    React.useEffect(() => {
        if (
            locationId &&
            previousLocationId &&
            locationId !== previousLocationId
        ) {
            const keptDocks = docks.filter((dockId) => dockId === "mobile");
            setValue("docks", keptDocks);
        }
    }, [locationId, previousLocationId, docks, setValue]);

    const onSubmit = async (droneConfigUpdate) => {
        droneConfigUpdate.docks = droneConfigUpdate.docks.map((dockId) => ({
            id: dockId,
        }));
        droneConfigUpdate.dockId = "deprecated"; // TODO: Remove when we are fully on routeplanner v3

        // These updates are just for backwards compatibility to also manage the settings on the old locations
        // TODO remove after routeplanner routeplanner V2 is not used anymore
        droneConfigUpdate.bufferDistance =
            droneConfigUpdate.routeplanner.buffer_distance;
        droneConfigUpdate.max_flight_duration =
            droneConfigUpdate.routeplanner.max_flight_duration;
        droneConfigUpdate.scan_speed =
            droneConfigUpdate.routeplanner.scan_speed;
        droneConfigUpdate.skipTakeOff =
            droneConfigUpdate.routeplanner.skip_take_off;
        droneConfigUpdate.takeoff_height =
            droneConfigUpdate.routeplanner.takeoff_height;
        droneConfigUpdate.takeoff_buffer =
            droneConfigUpdate.routeplanner.takeoff_buffer;

        droneConfigUpdate.focal_length =
            droneConfigUpdate.payload.camera_settings.focal_length;
        droneConfigUpdate.sensor_height =
            droneConfigUpdate.payload.camera_settings.sensor_height;
        droneConfigUpdate.sensor_width =
            droneConfigUpdate.payload.camera_settings.sensor_width;
        droneConfigUpdate.max_focus_distance =
            droneConfigUpdate.payload.camera_settings.max_focus_distance;
        droneConfigUpdate.min_focus_distance =
            droneConfigUpdate.payload.camera_settings.min_focus_distance;
        droneConfigUpdate.z_offset =
            droneConfigUpdate.payload.camera_settings.z_offset;

        // TODO remove after the routeplanner V2 is not used anymore and routeplanner V3 uses the travel speed setting
        droneConfigUpdate.max_speed =
            droneConfigUpdate.routeplanner.travel_speed;
        droneConfigUpdate.routeplanner.max_speed =
            droneConfigUpdate.routeplanner.travel_speed;

        // console.log(droneConfigUpdate)
        await DroneConfigDocument(droneId).set(droneConfigUpdate, {
            merge: true,
        });
        navigate(
            urls.locationDrone(
                droneConfigUpdate.organizationId,
                droneConfigUpdate.locationId,
                droneId,
            ),
        );
    };

    return (
        <Layout>
            <title>{`${droneId} Settings | MyCorvus`}</title>
            <Breadcrumbs path={breadcrumbPaths.droneSettings} />
            <Card>
                <Title
                    title={
                        <>
                            <BackButton
                                to={urls.locationDrone(
                                    originalOrganizationId,
                                    originalLocationId,
                                    droneId,
                                )}
                            />
                            {droneId} Settings
                        </>
                    }
                />
                <Card.Body className="p-4 py-0">
                    {isLoading ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            {getUserIsAdmin() && (
                                <>
                                    <Button
                                        variant="success"
                                        disabled={isSubmitting || !isDirty}
                                        type="submit"
                                        className="mb-3 mt-1"
                                    >
                                        <FaSave className="mb-1 me-1" />
                                        {isSubmitting ? "Saving..." : "Save"}
                                    </Button>
                                    <CancelButton
                                        className="ms-2 mb-3 mt-1"
                                        to={urls.locationDrone(
                                            originalOrganizationId,
                                            originalLocationId,
                                            droneId,
                                        )}
                                    />
                                    <SelectFormGroup
                                        label="Organization"
                                        name="organizationId"
                                        options={organizations}
                                        control={control}
                                        errors={errors}
                                    />
                                </>
                            )}
                            <SelectFormGroup
                                label="Location"
                                name="locationId"
                                options={locations.filter(
                                    (location) => !location.deleted,
                                )}
                                disabledOption={{
                                    id: "none",
                                    name: "Select a location...",
                                }}
                                control={control}
                                errors={errors}
                            />
                            {getUserIsAdmin() && (
                                <SelectFormGroup
                                    label="Payload"
                                    name="payloadId"
                                    options={payloads}
                                    disabledOption={{
                                        id: "none",
                                        name: "Select a payload...",
                                    }}
                                    control={control}
                                    errors={errors}
                                />
                            )}
                            <ReactSelectFormGroup
                                label="Dock"
                                name="docks"
                                options={[
                                    { id: "mobile", name: "Mobile Dock" },
                                    ...locationDocks,
                                ]}
                                isMulti={true}
                                placeholder={"No Dock"}
                                control={control}
                                errors={errors}
                            />
                            {getUserIsAdmin() && (
                                <>
                                    <SelectFormGroup
                                        label="Augmentation"
                                        name="augmentation"
                                        options={[
                                            { id: "none", name: "None" },
                                            {
                                                id: "SIMULATION",
                                                name: "Simulation",
                                            },
                                            {
                                                id: "EMULATION",
                                                name: "Emulation",
                                            },
                                            {
                                                id: "STATIC_CAM",
                                                name: "Static camera",
                                            },
                                        ]}
                                        control={control}
                                        errors={errors}
                                    />
                                    <SelectFormGroup
                                        label="Model"
                                        name="model"
                                        options={[
                                            { id: "E12", name: "E12" },
                                            { id: "E13", name: "E13" },
                                        ]}
                                        control={control}
                                        errors={errors}
                                    />
                                </>
                            )}
                            {getUserIsAdmin() && (
                                <>
                                    <br />
                                    <h4>Navigation Control</h4>
                                    <h6>Control settings</h6>
                                    <TextFormGroup
                                        label="P forward"
                                        name="navcontrol.control_settings.P_forward"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.P_forward
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="P sideways"
                                        name="navcontrol.control_settings.P_sideways"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.P_sideways
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="D forward"
                                        name="navcontrol.control_settings.D_forward"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.D_forward
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="D sideways"
                                        name="navcontrol.control_settings.D_sideways"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.D_sideways
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Pxy"
                                        name="navcontrol.control_settings.Pxy"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.Pxy
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Dxy"
                                        name="navcontrol.control_settings.Dxy"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.Dxy
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Ixy"
                                        name="navcontrol.control_settings.Ixy"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.Ixy
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Pz"
                                        name="navcontrol.control_settings.Pz"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.Pz
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="vxy max"
                                        name="navcontrol.control_settings.vxy_max"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.vxy_max
                                        }
                                        setValue={setValue}
                                        unitText="m/s"
                                    />
                                    <TextFormGroup
                                        label="vz max"
                                        name="navcontrol.control_settings.vz_max"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.vz_max
                                        }
                                        setValue={setValue}
                                        unitText="m/s"
                                    />
                                    <TextFormGroup
                                        label="Landing speed"
                                        name="navcontrol.control_settings.landing_speed"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.landing_speed
                                        }
                                        setValue={setValue}
                                        unitText="m/s"
                                    />
                                    <TextFormGroup
                                        label="Dock landing speed"
                                        name="navcontrol.control_settings.dock_landing_speed"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.dock_landing_speed
                                        }
                                        setValue={setValue}
                                        unitText="m/s"
                                    />
                                    <TextFormGroup
                                        label="Max forward acceleration"
                                        name="navcontrol.control_settings.a_forward_max"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.a_forward_max
                                        }
                                        setValue={setValue}
                                        unitText="m/s²"
                                    />
                                    <TextFormGroup
                                        label="Braking distance xy"
                                        name="navcontrol.control_settings.braking_distance_xy"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.braking_distance_xy
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Braking distance z"
                                        name="navcontrol.control_settings.braking_distance_z"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.control_settings
                                                ?.braking_distance_z
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />

                                    <br />
                                    <h6>Preferences</h6>
                                    <BooleanFormGroup
                                        label="Auto sleep"
                                        name="navcontrol.preferences.auto_sleep"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.auto_sleep
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Auto sleep time"
                                        name="navcontrol.preferences.auto_sleep_time_sec"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.auto_sleep_time_sec
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />
                                    <BooleanFormGroup
                                        label="Auto shutdown"
                                        name="navcontrol.preferences.auto_shutdown"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.auto_shutdown
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Auto shutdown time"
                                        name="navcontrol.preferences.auto_shutdown_seconds"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.auto_shutdown_seconds
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />
                                    <BooleanFormGroup
                                        label="Docking charge check (only rpv2)"
                                        name="navcontrol.preferences.is_dock_success_based_on_charging"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.is_dock_success_based_on_charging
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Reboot on connection loss"
                                        name="navcontrol.preferences.reboot_on_connection_loss"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.reboot_on_connection_loss
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Reboot on connection loss time"
                                        name="navcontrol.preferences.reboot_on_connection_loss_seconds"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.reboot_on_connection_loss_seconds
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />
                                    <BooleanFormGroup
                                        label="Skip calibration"
                                        name="navcontrol.preferences.skip_calibration"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.skip_calibration
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Automatically abort tasks (rpv3)"
                                        name="navcontrol.preferences.use_task_auto_abort"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.use_task_auto_abort
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Remaining flight duration factor (v2 battery)"
                                        name="navcontrol.preferences.remaining_flight_duration_factor"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.remaining_flight_duration_factor
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Remaining flight duration margin (v2 battery)"
                                        name="navcontrol.preferences.remaining_flight_duration_margin"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.remaining_flight_duration_margin
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />
                                    <TextFormGroup
                                        label="Remaining flight duration factor (v3 battery)"
                                        name="navcontrol.preferences.remaining_flight_duration_factor_v3"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.remaining_flight_duration_factor_v3
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Remaining flight duration margin (v3 battery)"
                                        name="navcontrol.preferences.remaining_flight_duration_margin_v3"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preferences
                                                ?.remaining_flight_duration_margin_v3
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />

                                    <br />
                                    <h6>Preflight checks</h6>
                                    <BooleanFormGroup
                                        label="Battery percentage"
                                        name="navcontrol.preflight_checks.battery_percent_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.battery_percent_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Minimum battery percentage"
                                        name="navcontrol.preflight_checks.min_battery_percentage"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.min_battery_percentage
                                        }
                                        setValue={setValue}
                                        unitText="%"
                                    />
                                    <BooleanFormGroup
                                        label="Stable position"
                                        name="navcontrol.preflight_checks.position_stable_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.position_stable_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Recent position"
                                        name="navcontrol.preflight_checks.position_recent_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.position_recent_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Accurate position"
                                        name="navcontrol.preflight_checks.position_accurate_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.position_accurate_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Remote control connected"
                                        name="navcontrol.preflight_checks.remote_connected_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.remote_connected_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Start only between -0.7m and 0.7m height"
                                        name="navcontrol.preflight_checks.home_position_z_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.home_position_z_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Hires exposure"
                                        name="navcontrol.preflight_checks.hires_exposure_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.hires_exposure_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Hires exposure lower bound"
                                        name="navcontrol.preflight_checks.hires_exposure_lower_bound"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.hires_exposure_lower_bound
                                        }
                                        setValue={setValue}
                                        unitText="μs"
                                    />
                                    <TextFormGroup
                                        label="Hires exposure upper bound"
                                        name="navcontrol.preflight_checks.hires_exposure_upper_bound"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.hires_exposure_upper_bound
                                        }
                                        setValue={setValue}
                                        unitText="μs"
                                    />

                                    <BooleanFormGroup
                                        label="Flight mode"
                                        name="navcontrol.preflight_checks.current_flightmode_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.current_flightmode_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="CPU temperature"
                                        name="navcontrol.preflight_checks.cpu_temperature_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.cpu_temperature_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Start close to first waypoint"
                                        name="navcontrol.preflight_checks.start_close_to_first_waypoint_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.start_close_to_first_waypoint_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="TOF sensor health"
                                        name="navcontrol.preflight_checks.tof_sensors_healthy_enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.tof_sensors_healthy_enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Minimum disk space available"
                                        name="navcontrol.preflight_checks.min_disk_available_mb"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.preflight_checks
                                                ?.min_disk_available_mb
                                        }
                                        setValue={setValue}
                                        unitText="MB"
                                    />

                                    <br />
                                    <h4>Obstacle avoidance</h4>
                                    <BooleanFormGroup
                                        label="Enabled"
                                        name="navcontrol.obstacle_avoidance.enabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.enabled
                                        }
                                        setValue={setValue}
                                    />
                                    <h6>Controller</h6>
                                    <TextFormGroup
                                        label="Max horizontal avoidance speed"
                                        name="navcontrol.obstacle_avoidance.controller.avoidance_max_speed_xy"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.controller
                                                ?.avoidance_max_speed_xy
                                        }
                                        setValue={setValue}
                                        unitText="m/s"
                                    />
                                    <TextFormGroup
                                        label="Lookahead distance"
                                        name="navcontrol.obstacle_avoidance.controller.lookahead_distance_m"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.controller
                                                ?.lookahead_distance_m
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Progress timeout"
                                        name="navcontrol.obstacle_avoidance.controller.progress_timeout_seconds"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.controller
                                                ?.progress_timeout_seconds
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />
                                    <TextFormGroup
                                        label="Wait for clear"
                                        name="navcontrol.obstacle_avoidance.controller.wait_for_clear_seconds"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.controller
                                                ?.wait_for_clear_seconds
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />

                                    <h6>Corridor</h6>
                                    <TextFormGroup
                                        label="Planning margin"
                                        name="navcontrol.obstacle_avoidance.corridor.planning_margin_m"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.corridor?.planning_margin_m
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Width"
                                        name="navcontrol.obstacle_avoidance.corridor.width_m"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.corridor?.width_m
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Z min"
                                        name="navcontrol.obstacle_avoidance.corridor.z_min_m"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.corridor?.z_min_m
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Z max"
                                        name="navcontrol.obstacle_avoidance.corridor.z_max_m"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.corridor?.z_max_m
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />

                                    <h6>Planning grid</h6>
                                    <TextFormGroup
                                        label="Horizontal clearance"
                                        name="navcontrol.obstacle_avoidance.planning_grid.clearance_xy_m"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.planning_grid?.clearance_xy_m
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Vertical clearance"
                                        name="navcontrol.obstacle_avoidance.planning_grid.clearance_z_m"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.navcontrol?.obstacle_avoidance
                                                ?.planning_grid?.clearance_z_m
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />

                                    <br />
                                    <h4>Position Control</h4>
                                    <h6>Camera settings</h6>
                                    <TextFormGroup
                                        label="Max desired gain"
                                        name="poscontrol.camera.max_desired_gain"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.camera
                                                ?.max_desired_gain
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Max desired exposure"
                                        name="poscontrol.camera.max_desired_exposure"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.camera
                                                ?.max_desired_exposure
                                        }
                                        setValue={setValue}
                                        unitText="µs"
                                    />
                                    <TextFormGroup
                                        label="Exposure gain offset"
                                        name="poscontrol.camera.exposure_gain_offset"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.camera
                                                ?.exposure_gain_offset
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Maximum gain"
                                        name="poscontrol.camera.max_gain"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.camera?.max_gain
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Minimum gain"
                                        name="poscontrol.camera.min_gain"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.camera?.min_gain
                                        }
                                        setValue={setValue}
                                    />

                                    <br />
                                    <h6>Exposure settings</h6>
                                    <TextFormGroup
                                        label="Marker msv"
                                        name="poscontrol.exposure.marker_msv"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.exposure
                                                ?.marker_msv
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Tracking msv"
                                        name="poscontrol.exposure.tracking_msv"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.exposure
                                                ?.tracking_msv
                                        }
                                        setValue={setValue}
                                    />

                                    <br />
                                    <h6>Calculation settings</h6>
                                    <BooleanFormGroup
                                        label="Allow 1 marker calculations"
                                        name="poscontrol.calculation.allow_1_marker_calculations"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.calculation
                                                ?.allow_1_marker_calculations
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Ignore markers 3m above drone"
                                        name="poscontrol.calculation.ignore_markers_far_above_drone"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.calculation
                                                ?.ignore_markers_far_above_drone
                                        }
                                        setValue={setValue}
                                    />

                                    <br />
                                    <h6>Debug settings</h6>
                                    <TextFormGroup
                                        label="Debug save time"
                                        name="poscontrol.debug.debug_save_time"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.debug
                                                ?.debug_save_time
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />
                                    <TextFormGroup
                                        label="Situation image save time"
                                        name="poscontrol.debug.situation_image_save_time"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.poscontrol?.debug
                                                ?.situation_image_save_time
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />

                                    <br />
                                    <h4>Payload</h4>
                                    <h6>Camera settings</h6>
                                    <TextFormGroup
                                        label="Focal length"
                                        name="payload.camera_settings.focal_length"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.camera_settings
                                                ?.focal_length
                                        }
                                        setValue={setValue}
                                        unitText="mm"
                                    />
                                    <TextFormGroup
                                        label="Max focus distance"
                                        name="payload.camera_settings.max_focus_distance"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.camera_settings
                                                ?.max_focus_distance
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Min focus distance"
                                        name="payload.camera_settings.min_focus_distance"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.camera_settings
                                                ?.min_focus_distance
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Sensor height"
                                        name="payload.camera_settings.sensor_height"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.camera_settings
                                                ?.sensor_height
                                        }
                                        setValue={setValue}
                                        unitText="mm"
                                    />
                                    <TextFormGroup
                                        label="Sensor width"
                                        name="payload.camera_settings.sensor_width"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.camera_settings
                                                ?.sensor_width
                                        }
                                        setValue={setValue}
                                        unitText="mm"
                                    />
                                    <TextFormGroup
                                        label="Z offset"
                                        name="payload.camera_settings.z_offset"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.camera_settings
                                                ?.z_offset
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <BooleanFormGroup
                                        label="Payload camera"
                                        name="payload.camera_settings.use_front_camera"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.camera_settings
                                                ?.use_front_camera
                                        }
                                        setValue={setValue}
                                        trueLabel="Front camera"
                                        falseLabel="Downward camera"
                                    />
                                </>
                            )}
                            <br />
                            <h6>Image uploader</h6>
                            <BooleanFormGroup
                                label="Uploading images"
                                name="payload.uploader.is_disabled"
                                control={control}
                                errors={errors}
                                defaultValue={
                                    droneConfigPresets?.[model]?.default
                                        ?.payload?.uploader?.is_disabled
                                }
                                setValue={setValue}
                                trueLabel="Disabled"
                                falseLabel="Enabled"
                            />
                            {getUserIsAdmin() && (
                                <>
                                    <SelectFormGroup
                                        label="Upload strategy"
                                        name="payload.uploader.upload_strategy"
                                        options={[
                                            {
                                                id: "oldest_first",
                                                name: "Oldest first",
                                            },
                                            {
                                                id: "newest_first",
                                                name: "Newest first",
                                            },
                                        ]}
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.uploader
                                                ?.upload_strategy
                                        }
                                        setValue={setValue}
                                    />
                                    <SelectFormGroup
                                        label="Upload order within flight folder"
                                        name="payload.uploader.order_in_flight_folder"
                                        options={[
                                            { id: "random", name: "Random" },
                                            {
                                                id: "oldest_first",
                                                name: "Oldest first",
                                            },
                                            {
                                                id: "newest_first",
                                                name: "Newest first",
                                            },
                                        ]}
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.uploader
                                                ?.order_in_flight_folder
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Uploads per second"
                                        placeholder="0 means no limiting of upload rate"
                                        name="payload.uploader.uploads_per_second"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.payload?.uploader
                                                ?.uploads_per_second
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Prioritized flight ids"
                                        placeholder="Comma separated flight ids"
                                        name="payload.uploader.upload_priority_ids"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[
                                                model
                                            ]?.default?.payload?.uploader?.upload_priority_ids?.join(
                                                ",",
                                            ) ?? undefined
                                        }
                                        setValue={setValue}
                                    />
                                    <br />
                                    <h4>Telemetry</h4>
                                    <BooleanFormGroup
                                        label="Uploading telemetry"
                                        name="telemetry.uploader.is_disabled"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.telemetry?.uploader
                                                ?.is_disabled
                                        }
                                        setValue={setValue}
                                        trueLabel="Disabled"
                                        falseLabel="Enabled"
                                    />
                                    <br />
                                    <h4>Powermodule</h4>
                                    <BooleanFormGroup
                                        label="Battery percentage"
                                        name="powermodule.capacity_based_battery"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.powermodule
                                                ?.capacity_based_battery
                                        }
                                        setValue={setValue}
                                        trueLabel="Based on capacity"
                                        falseLabel="Based on voltage"
                                    />
                                    <br />
                                    <h4>Route planner</h4>
                                    <SelectFormGroup
                                        label="Version"
                                        name="routeplanner.version"
                                        options={routeplannerVersions}
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner?.version
                                        }
                                        setValue={setValue}
                                    />
                                    <TextFormGroup
                                        label="Buffer distance"
                                        name="routeplanner.buffer_distance"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner?.buffer_distance
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Min battery percentage"
                                        name="routeplanner.min_battery_percentage"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner
                                                ?.min_battery_percentage
                                        }
                                        setValue={setValue}
                                        unitText="%"
                                    />
                                    <TextFormGroup
                                        label="Max flight duration"
                                        name="routeplanner.max_flight_duration"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner
                                                ?.max_flight_duration
                                        }
                                        setValue={setValue}
                                        unitText="seconds"
                                    />
                                    <TextFormGroup
                                        label="Travel speed"
                                        name="routeplanner.travel_speed"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner?.travel_speed
                                        }
                                        setValue={setValue}
                                        unitText="m/s"
                                    />
                                    <TextFormGroup
                                        label="Scan speed"
                                        name="routeplanner.scan_speed"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner?.scan_speed
                                        }
                                        setValue={setValue}
                                        unitText="m/s"
                                    />
                                    <BooleanFormGroup
                                        label="Resume interrupted scan line"
                                        name="routeplanner.resume_interrupted_line_scan"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner
                                                ?.resume_interrupted_line_scan
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Camera box - remove xyz commands in takeoff"
                                        name="routeplanner.skip_take_off"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner?.skip_take_off
                                        }
                                        setValue={setValue}
                                    />

                                    <TextFormGroup
                                        label="Takeoff height (only rpv2)"
                                        name="routeplanner.takeoff_height"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner?.takeoff_height
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Takeoff buffer (only rpv2)"
                                        name="routeplanner.takeoff_buffer"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner?.takeoff_buffer
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />

                                    <h6>Low speed flyzone</h6>
                                    <TextFormGroup
                                        label="Speed"
                                        name="routeplanner.low_speed_flyzone.speed"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner
                                                ?.low_speed_flyzone?.speed
                                        }
                                        setValue={setValue}
                                        unitText="m/s"
                                    />
                                    <TextFormGroup
                                        label="Buffer distance xy"
                                        name="routeplanner.low_speed_flyzone.buffer_distance_xy"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner
                                                ?.low_speed_flyzone
                                                ?.buffer_distance_xy
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <TextFormGroup
                                        label="Buffer distance z"
                                        name="routeplanner.low_speed_flyzone.buffer_distance_z"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.routeplanner
                                                ?.low_speed_flyzone
                                                ?.buffer_distance_z
                                        }
                                        setValue={setValue}
                                        unitText="m"
                                    />
                                    <br />
                                    <h4>Streamer</h4>
                                    <BooleanFormGroup
                                        label="Enable hires stream"
                                        name="streamer.enable_hires_stream"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.streamer?.enable_hires_stream
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Enable payload stream"
                                        name="streamer.enable_payload_stream"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.streamer
                                                ?.enable_payload_stream
                                        }
                                        setValue={setValue}
                                    />
                                    <BooleanFormGroup
                                        label="Enable qvio stream"
                                        name="streamer.enable_qvio_stream"
                                        control={control}
                                        errors={errors}
                                        defaultValue={
                                            droneConfigPresets?.[model]?.default
                                                ?.streamer?.enable_qvio_stream
                                        }
                                        setValue={setValue}
                                    />
                                </>
                            )}
                            <Button
                                variant="success"
                                disabled={isSubmitting || !isDirty}
                                type="submit"
                                className="mb-3 mt-1"
                            >
                                <FaSave className="mb-1 me-1" />
                                {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                            <CancelButton
                                className="ms-2 mb-3 mt-1"
                                to={urls.locationDrone(
                                    originalOrganizationId,
                                    originalLocationId,
                                    droneId,
                                )}
                            />
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </Layout>
    );
}
